import { Button } from "@safers/ui/atoms";
import { useOnboarding, useDismissible, installGuideText } from "@safers/pwa";
import { usePushSubscription } from "@/hooks/usePushSubscription";

const DISMISS_KEY = "safers-install-dismissed";

/**
 * 화면 하단 PWA 설치·알림 온보딩 배너.
 *
 * 환경(OS·브라우저·설치 여부·알림 권한)을 판정해 **지금 해야 할 행동 하나만** 보여준다.
 * 단계 순서와 환경별 제약은 `@safers/pwa` 와 이슈 #176 참조.
 *
 * 설계 원칙 — 어떤 환경에서도 침묵하지 않는다. 설치도 알림도 불가능한 환경이라면
 * "불가능하다"는 사실 자체를 알린다. 못 받는 사람이 그걸 모르는 것이 가장 위험하다.
 */
export function InstallBanner() {
  const { subscribe } = usePushSubscription(null);
  const { step, env, installed } = useOnboarding({ pushProbe: subscribe });
  const { dismissed, dismiss } = useDismissible(DISMISS_KEY);

  if (dismissed || step.kind === "done") return null;

  const content = (() => {
    switch (step.kind) {
      case "switch-browser":
        return step.target === "chrome"
          ? {
              title: "Chrome에서 열어주세요",
              body: "현재 브라우저에서는 앱 설치와 알림이 제한됩니다.",
              action: step.openInChrome && { label: "Chrome으로 열기", onClick: step.openInChrome },
            }
          : {
              title: "Safari에서 열어주세요",
              body: "iPhone은 Safari에서만 앱 설치와 알림을 사용할 수 있습니다.",
              action: null,
            };

      case "install":
        return step.manual
          ? {
              title: env.os === "ios" ? "홈 화면에 추가" : "앱으로 설치하면 더 편해요",
              body: installGuideText(env.os),
              action: null,
            }
          : {
              title: "Safers 앱 설치",
              body: "홈 화면에 추가하고 알림을 받아보세요.",
              action: step.promptInstall && {
                label: "설치",
                onClick: () => void step.promptInstall?.(),
              },
            };

      case "open-app":
        return step.justInstalled
          ? {
              title: "설치 완료",
              body: "이제 설치된 Safers 앱으로 열어주세요. 알림도 앱에서 더 안정적으로 받습니다.",
              action: null,
            }
          : {
              title: "앱으로 여세요",
              body:
                env.os === "desktop"
                  ? "설치된 Safers 앱에서 열면 더 빠르고 알림이 안정적입니다."
                  : "홈 화면의 Safers 아이콘으로 열면 더 빠르고 알림이 안정적입니다.",
              action: null,
            };

      case "enable-notification":
        return {
          title: installed ? "설치 완료" : "알림 받기",
          body: installed
            ? "알림을 켜면 위험 상황을 즉시 받아볼 수 있습니다."
            : "안전 이벤트가 발생하면 즉시 알려드립니다.",
          action: { label: "알림 켜기", onClick: () => void step.request() },
        };

      case "push-unavailable":
        if (step.reason === "denied") {
          return {
            title: "알림이 차단되어 있습니다",
            body: "브라우저 설정에서 이 사이트의 알림을 허용해야 이벤트를 받을 수 있습니다.",
            action: null,
          };
        }
        return {
          title: "이 기기에서는 알림을 받을 수 없습니다",
          body: "안전 이벤트를 놓치지 않도록 앱을 주기적으로 확인해주세요.",
          action: null,
        };
    }
  })();

  return (
    <div
      role="dialog"
      aria-label="앱 설치 및 알림 안내"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-neutral-200 bg-white px-4 py-3 shadow-lg dark:border-neutral-700 dark:bg-neutral-900"
    >
      <div className="mx-auto flex max-w-screen-sm items-center gap-3">
        <img src="/icons/icon-192.png" alt="" className="h-10 w-10 shrink-0 rounded-lg" />

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">
            {content.title}
          </p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">{content.body}</p>
        </div>

        {content.action && (
          <Button size="sm" onClick={content.action.onClick}>
            {content.action.label}
          </Button>
        )}

        <button
          type="button"
          onClick={dismiss}
          aria-label="닫기"
          className="shrink-0 rounded-md p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
          data-env={`${env.os}/${env.browser}`}
        >
          ✕
        </button>
      </div>
    </div>
  );
}
